"use client";

/**
 * Pro for feedback (owner, 2026-10-12): "it should not be a tiring one,
 * without friction... pre-suggested categories, but users have to fill
 * them in themselves... only for a limited time... if you are already on
 * a trial, we say you want to prolong the trial for an extra month."
 *
 * The hub card offers it while the offer runs and the account can take
 * it (backend routes/feedback_offer.py decides: state "open"). The form
 * is three questions: topic chips (optional), two short texts in the
 * person's own words (required), and where they found us (optional). An
 * admin reads each answer and grants the month by hand.
 *
 * `/?feedback=1` opens the form straight away, for links in emails and
 * Telegram, through `FeedbackOfferLink` in the layout: it works signed
 * out too, because the e-mail's link carries the person's own feedback
 * token (`t`), which the backend accepts in place of a session for
 * these two endpoints only.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { API_BASE, fetchAPI } from "@/lib/api";
import { useLocale, useT } from "@/lib/locale-context";
import type { TranslationKey } from "@/lib/i18n";
import { shared, invalidateShared } from "@/lib/shared-get";
import { peekHubBoot } from "@/lib/hub-boot";
import { useAuth } from "@/lib/auth-context";
import { trackEvent } from "@/lib/tracking";
import BottomSheet from "@/components/BottomSheet";
import { useIsMobileViewport } from "@/lib/use-install-prompt";
import { SELECTED, UNSELECTED } from "@/lib/ui-classes";

export interface OfferState {
  active: boolean;
  until: string;
  bonus_days: number;
  state: "anon" | "unverified" | "paid" | "submitted" | "granted" | "open" | "closed" | "later";
  trial_active: boolean;
  trial_days_left: number | null;
  topics: string[];
  sources: string[];
  name?: string | null;
  via_token?: boolean;
  pro_active?: boolean;
  /** How many more times the site-wide prompt may come up for the
   *  account (6 per account, counted by the API); null signed out. */
  prompt_left?: number | null;
}

const MIN = 15;
const HIDE_KEY = "menzil_fbo_later";
const HIDE_DAYS = 7;
/** Fired on window when an answer has been sent from any of the three
 *  entry points (hub card, e-mail link, floating prompt), so the others
 *  re-read the offer and go. */
export const OFFER_CHANGED_EVENT = "fbo:changed";
export function announceOfferChanged(): void {
  invalidateShared("/feedback-offer");
  try { window.dispatchEvent(new Event(OFFER_CHANGED_EVENT)); } catch { /* no window */ }
}

/** Shared read: the hub starts it early so the card does not push the page.
 * With a link token the read is for that token's account and is not shared. */
export function loadOffer(token?: string | null): Promise<OfferState> {
  if (token) return fetchAPI<OfferState>("/feedback-offer", { t: token });
  return shared("/feedback-offer", () => fetchAPI<OfferState>("/feedback-offer"), { reuseMs: 60_000 });
}

/** The site-wide prompt came up: the API counts it against the account's
 *  shows and answers how many are left (null when the count failed). */
export async function reportPromptShown(): Promise<number | null> {
  try {
    const res = await fetch(`${API_BASE}/feedback-offer/shown`, { method: "POST", credentials: "include" });
    if (!res.ok) return null;
    const data = (await res.json()) as { prompt_left?: unknown };
    return typeof data.prompt_left === "number" ? data.prompt_left : null;
  } catch {
    return null;
  } finally {
    // The next read (the hub card, another page) asks again.
    invalidateShared("/feedback-offer");
  }
}

export function dateLabel(iso: string, loc: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  if (loc === "en") return d.toLocaleDateString("en-GB", { day: "numeric", month: "long" });
  if (loc === "ru") return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
  const months = ["yanvar", "fevral", "mart", "aprel", "may", "iyun", "iyul", "avqust", "sentyabr", "oktyabr", "noyabr", "dekabr"];
  return `${d.getDate()} ${months[d.getMonth()]}`;
}

export const fill = (s: string, v: Record<string, string | number>) =>
  s.replace(/\{(\w+)\}/g, (m, k) => (k in v ? String(v[k]) : m));

/** "Later" was pressed within the last HIDE_DAYS. */
function hiddenForNow(): boolean {
  try {
    const at = Number(localStorage.getItem(HIDE_KEY) || 0);
    return at > 0 && Date.now() - at < HIDE_DAYS * 86_400_000;
  } catch { return false; }
}

/** The hub's card. It renders in the browser only (the signed-in hub), so
 *  its first render starts from what is known already: the offer the
 *  hub's bootstrap brought (lib/hub-boot) and the "later" mark. Read in
 *  an effect, the card appeared a frame after the widgets and pushed
 *  them down. */
export default function FeedbackOfferCard() {
  const t = useT();
  const { locale } = useLocale();
  const { user } = useAuth();
  const [offer, setOffer] = useState<OfferState | null>(() => (peekHubBoot(user?.id)?.offer as OfferState | undefined) ?? null);
  const [hidden, setHidden] = useState(() => (typeof window === "undefined" ? true : hiddenForNow()));
  const [open, setOpen] = useState(false);
  const sent = useRef(false);

  useEffect(() => {
    let alive = true;
    loadOffer().then((o) => { if (alive) setOffer(o); }).catch(() => {});
    setHidden(hiddenForNow());
    return () => { alive = false; };
  }, []);

  if (!offer || offer.state !== "open") return null;
  const trial = offer.trial_active;
  const vars = { days: offer.bonus_days, date: dateLabel(offer.until, locale) };

  return (
    <>
      {!hidden && (
        <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="min-w-0">
            <p className="text-[15px] font-semibold tracking-tight text-gray-900">
              {t(offer.pro_active ? "fbo.title.pro" : trial ? "fbo.title.trial" : "fbo.title")}
            </p>
            <p className="mt-0.5 text-sm leading-relaxed text-gray-600">{fill(t(offer.pro_active ? "fbo.text.pro" : trial ? "fbo.text.trial" : "fbo.text"), vars)}</p>
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={() => { trackEvent("fbo.open", { trial }); setOpen(true); }}
              className="rounded-lg border border-gray-200 bg-white text-gray-800 hover:border-gray-300 hover:bg-gray-50 px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              {t("fbo.cta")}
            </button>
            <button
              type="button"
              onClick={() => {
                trackEvent("fbo.later", {});
                try { localStorage.setItem(HIDE_KEY, String(Date.now())); } catch { /* this page only */ }
                setHidden(true);
              }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              {t("fbo.later")}
            </button>
          </div>
        </div>
      )}
      {open && (
        <FeedbackOfferForm
          offer={offer}
          onClose={() => {
            setOpen(false);
            // The card goes once the thank-you is closed; switching the
            // state earlier remounted the form over its own thank-you.
            if (sent.current) setOffer({ ...offer, state: "submitted" });
          }}
          onSent={() => {
            sent.current = true;
            announceOfferChanged();
          }}
        />
      )}
    </>
  );
}

type Phase = "form" | "sending" | "sent";

/**
 * The deep link `/?feedback=1[&t=<token>]`, on any page and for anyone:
 * reads the token once, drops it from the address bar (it must not
 * travel on through referrers or analytics), and opens the form for the
 * account the token names. Mounted once in the locale layout.
 */
export function FeedbackOfferLink() {
  const [offer, setOffer] = useState<OfferState | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    let tok: string | null = null;
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.get("feedback") !== "1") return undefined;
      tok = u.searchParams.get("t");
      u.searchParams.delete("feedback");
      u.searchParams.delete("t");
      window.history.replaceState(window.history.state, "", `${u.pathname}${u.search}${u.hash}`);
    } catch {
      return undefined;
    }
    setToken(tok);
    setOpen(true);
    trackEvent("fbo.link", { token: Boolean(tok) });
    loadOffer(tok).then((o) => { if (alive) setOffer(o); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!open || !offer) return null;
  return (
    <FeedbackOfferForm
      offer={offer}
      token={token}
      onClose={() => setOpen(false)}
      onSent={announceOfferChanged}
    />
  );
}

export function FeedbackOfferForm({ offer, token, onClose, onSent }: { offer: OfferState; token?: string | null; onClose: () => void; onSent?: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const mobile = useIsMobileViewport();
  const [topics, setTopics] = useState<string[]>([]);
  const [change, setChange] = useState("");
  const [add, setAdd] = useState("");
  const [source, setSource] = useState<string | null>(null);
  const [sourceDetail, setSourceDetail] = useState("");
  const [topicDetail, setTopicDetail] = useState("");
  const [phase, setPhase] = useState<Phase>("form");
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const [sentTrial, setSentTrial] = useState(offer.trial_active);

  const trial = offer.trial_active;
  const vars = { days: offer.bonus_days, date: dateLabel(offer.until, locale), n: MIN };
  const shortChange = change.trim().length < MIN;
  const shortAdd = add.trim().length < MIN;
  // "A friend" needs who, "somewhere else" needs where, "something else"
  // among the topics needs what; the button waits for all of it.
  const needsSourceDetail = source === "friend" || source === "other";
  const needsTopicDetail = topics.includes("other");
  const ready =
    !shortChange && !shortAdd &&
    (!needsSourceDetail || sourceDetail.trim().length > 0) &&
    (!needsTopicDetail || topicDetail.trim().length > 0);

  // Desktop: Escape closes and the page stops scrolling (the sheet does
  // both itself on phones).
  useEffect(() => {
    if (mobile) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [mobile, onClose]);

  const submit = async () => {
    setTried(true);
    if (shortChange || shortAdd) { setError(fill(t("fbo.err.short"), vars)); return; }
    setPhase("sending");
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/feedback-offer`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Locale": locale },
        body: JSON.stringify({
          topics, change, add, source, locale, token: token ?? undefined,
          source_detail: needsSourceDetail ? sourceDetail.trim() : undefined,
          topic_detail: needsTopicDetail ? topicDetail.trim() : undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = typeof body.detail === "string" ? body.detail : "";
        const key: TranslationKey =
          code === "too_short" ? "fbo.err.short"
          : code === "already_submitted" ? "fbo.err.already"
          : code === "offer_closed" ? "fbo.err.closed"
          : code === "detail_required" ? "fbo.err.detail"
          : "fbo.err.generic";
        setError(fill(t(key), vars));
        setPhase("form");
        return;
      }
      trackEvent("fbo.sent", { trial, topics: topics.length, source: source ?? "" });
      setSentTrial(trial);
      setPhase("sent");
      onSent?.();
      announceOfferChanged();
    } catch {
      setError(t("fbo.err.generic"));
      setPhase("form");
    }
  };

  // Opened from a link when the form is not for this account.
  const blocked =
    offer.state === "submitted" || offer.state === "granted" ? t("fbo.err.already")
    : offer.state === "closed" ? fill(t("fbo.err.closed"), vars)
    : offer.state === "paid" ? t("fbo.blocked.paid")
    : offer.state === "unverified" ? t("fbo.blocked.unverified")
    : offer.state === "anon" ? t("fbo.blocked.anon")
    : null;

  const title = t(offer.pro_active ? "fbo.title.pro" : trial ? "fbo.title.trial" : "fbo.title");
  const chip = (on: boolean) =>
    `h-9 rounded-lg border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
      on ? SELECTED : UNSELECTED
    }`;
  const field = (bad: boolean) =>
    `w-full resize-none rounded-xl border bg-white px-3 py-2.5 text-sm leading-6 text-gray-900 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-indigo-500 ${
      bad ? "border-rose-400" : "border-gray-200"
    }`;
  const left = (s: string) => Math.max(0, MIN - s.trim().length);

  let body: React.ReactNode;
  if (phase === "sent") {
    body = (
      <div className="py-2">
        <p className="text-base font-bold tracking-tight text-gray-900">{t("fbo.ok.title")}</p>
        <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{fill(t(offer.pro_active ? "fbo.ok.text.pro" : sentTrial ? "fbo.ok.text.trial" : "fbo.ok.text"), vars)}</p>
      </div>
    );
  } else if (blocked && offer.state !== "open") {
    body = <p className="py-2 text-sm leading-relaxed text-gray-600">{blocked}</p>;
  } else {
    body = (
      <div className="space-y-5">
        {offer.via_token && offer.name && (
          <p className="rounded-lg bg-gray-100 px-3 py-2 text-sm leading-relaxed text-gray-800">
            {fill(t("fbo.as"), { name: offer.name })}{" "}
            <a
              href={`/${locale}/auth/login?return_to=${encodeURIComponent(`/${locale}/?feedback=1`)}`}
              rel="nofollow"
              className="font-semibold underline underline-offset-2"
            >
              {t("fbo.notyou")}
            </a>
          </p>
        )}
        <p className="text-sm leading-relaxed text-gray-600">{fill(t("fbo.intro"), vars)}</p>

        <fieldset>
          <legend className="text-sm font-semibold text-gray-900">
            {t("fbo.q.topics")} <span className="font-normal text-gray-500">{t("fbo.optional")}</span>
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {offer.topics.map((k) => {
              const on = topics.includes(k);
              return (
                <button key={k} type="button" aria-pressed={on} className={chip(on)}
                  onClick={() => setTopics(on ? topics.filter((x) => x !== k) : [...topics, k])}>
                  {t(`fbo.topic.${k}` as TranslationKey)}
                </button>
              );
            })}
          </div>
          {needsTopicDetail && (
            <input
              type="text"
              value={topicDetail}
              maxLength={200}
              onChange={(e) => setTopicDetail(e.target.value)}
              placeholder={t("fbo.topic.detail")}
              aria-label={t("fbo.topic.detail")}
              className={`mt-2 h-10 w-full rounded-xl border bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-indigo-500 ${
                topicDetail.trim() ? "border-gray-200" : "border-gray-400"
              }`}
            />
          )}
        </fieldset>

        <label className="block">
          <span className="text-sm font-semibold text-gray-900">{t("fbo.q.change")}</span>
          <textarea rows={3} maxLength={1500} value={change} onChange={(e) => setChange(e.target.value)}
            placeholder={t("fbo.ph.change")} className={`mt-2 ${field(tried && shortChange)}`} />
          {change.length > 0 && shortChange && (
            <span className="mt-1 block text-xs tabular-nums text-gray-500">{fill(t("fbo.more"), { n: left(change) })}</span>
          )}
        </label>

        <label className="block">
          <span className="text-sm font-semibold text-gray-900">{t("fbo.q.add")}</span>
          <textarea rows={3} maxLength={1500} value={add} onChange={(e) => setAdd(e.target.value)}
            placeholder={t("fbo.ph.add")} className={`mt-2 ${field(tried && shortAdd)}`} />
          {add.length > 0 && shortAdd && (
            <span className="mt-1 block text-xs tabular-nums text-gray-500">{fill(t("fbo.more"), { n: left(add) })}</span>
          )}
        </label>

        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">{t("fbo.rule")}</p>

        <fieldset>
          <legend className="text-sm font-semibold text-gray-900">
            {t("fbo.q.source")} <span className="font-normal text-gray-500">{t("fbo.optional")}</span>
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {offer.sources.map((k) => (
              <button key={k} type="button" aria-pressed={source === k} className={chip(source === k)}
                onClick={() => { setSource(source === k ? null : k); setSourceDetail(""); }}>
                {t(`fbo.src.${k}` as TranslationKey)}
              </button>
            ))}
          </div>
          {needsSourceDetail && (
            <input
              type="text"
              value={sourceDetail}
              maxLength={200}
              onChange={(e) => setSourceDetail(e.target.value)}
              placeholder={t(source === "friend" ? "fbo.src.detail.friend" : "fbo.src.detail.other")}
              aria-label={t(source === "friend" ? "fbo.src.detail.friend" : "fbo.src.detail.other")}
              className={`mt-2 h-10 w-full rounded-xl border bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-indigo-500 ${
                sourceDetail.trim() ? "border-gray-200" : "border-gray-400"
              }`}
            />
          )}
        </fieldset>
      </div>
    );
  }

  const footer = phase === "sent" || (blocked && offer.state !== "open") ? (
    <button type="button" onClick={onClose}
      className="w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700">
      {t("fbo.close")}
    </button>
  ) : (
    <div className="space-y-2">
      {error && <p className="text-center text-sm text-rose-600" role="alert">{error}</p>}
      <button type="button" onClick={submit} disabled={!ready || phase === "sending"}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50">
        {phase === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}
        {t("fbo.submit")}
      </button>
      <p className="text-center text-xs leading-relaxed text-gray-500">{fill(t("fbo.fine"), vars)}</p>
    </div>
  );

  if (mobile) {
    return (
      <BottomSheet open onClose={onClose} title={title} footer={footer}>
        <div className="pb-2 pt-1">{body}</div>
      </BottomSheet>
    );
  }
  if (typeof document === "undefined") return null;
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={title} onClick={onClose}
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4 modal-backdrop-enter">
      <div onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-gray-200 bg-white shadow-[0_24px_64px_-24px_rgba(15,23,42,0.35)] modal-enter">
        <div className="flex items-start justify-between gap-3 px-6 pt-5">
          <p className="text-lg font-bold tracking-tight text-gray-900">{title}</p>
          <button type="button" onClick={onClose} aria-label={t("fbo.close")}
            className="-mr-2 rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-4">{body}</div>
        <div className="border-t border-gray-200 px-6 py-4">{footer}</div>
      </div>
    </div>,
    document.body,
  );
}
